import React, { useEffect } from 'react';
import styles from './Drawer.module.css';
import Ico from './Ico';
import Pill from './Pill';
import clx from './clx';

const ICO_CLOSE    = 'M18 6 6 18M6 6l12 12';
const ICO_CHEVLEFT = 'm15 18-6-6 6-6';
const ICO_CHEVRIGHT= 'm9 18 6-6-6-6';
const ICO_EXTERNAL = 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3';

export default function Drawer({
  open,
  onClose,
  breadcrumbs = [],
  backLabel,
  onBack,
  icon,
  iconEl,
  iconStyle,
  title,
  subtitle,
  titleMono = false,
  status,
  stats = [],
  footer,
  frameKey,
  children,
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const hasHeader = title || icon || iconEl;
  // The breadcrumb/back top row only earns its space when there's something
  // to put in it — otherwise it just strands the close button above an empty
  // gap. Simple, single-level drawers (no breadcrumbs, no back) fold the
  // close button into the header row instead.
  const showTopRow = breadcrumbs.length > 0 || !!onBack || !hasHeader;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.panel}>

        {/* breadcrumb + close row */}
        {showTopRow && (
          <div className={styles.topRow}>
            {onBack && (
              <button className={styles.backBtn} onClick={onBack} type="button">
                <Ico d={ICO_CHEVLEFT} size={13} />
                {backLabel}
              </button>
            )}
            <div style={{ flex: 1 }} />
            <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Close">
              <Ico d={ICO_CLOSE} size={16} />
            </button>
          </div>
        )}

        {breadcrumbs.length > 0 && (
          <div className={styles.crumbs}>
            {breadcrumbs.map((crumb, i) => (
              <React.Fragment key={i}>
                <button
                  className={styles.crumb}
                  onClick={crumb.onClick}
                  type="button"
                >
                  {crumb.label}
                </button>
                <Ico d={ICO_CHEVRIGHT} size={11} className={styles.crumbSep} />
              </React.Fragment>
            ))}
          </div>
        )}

        {/* header */}
        {hasHeader && (
          <div className={styles.header}>
            {(icon || iconEl) && (
              <span className={styles.iconTile} style={iconStyle}>
                {iconEl || <Ico d={icon} size={18} />}
              </span>
            )}
            <div className={styles.headerContent}>
              <div className={styles.titleRow}>
                <span className={clx(styles.title, titleMono && styles.titleMono)}>{title}</span>
                {status && (
                  <Pill status={status} dot>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </Pill>
                )}
              </div>
              {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
            </div>
            {!showTopRow && (
              <button className={styles.closeBtn} onClick={onClose} type="button" aria-label="Close">
                <Ico d={ICO_CLOSE} size={16} />
              </button>
            )}
          </div>
        )}

        {/* stat strip */}
        {stats.length > 0 && (
          <div className={styles.stats}>
            {stats.map((s, i) => (
              <div key={i} className={styles.stat}>
                <div className={styles.statLabel}>{s.label}</div>
                {s.href ? (
                  <a
                    className={styles.statLink}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {s.value}
                    <Ico d={ICO_EXTERNAL} size={11} strokeWidth={1.8} />
                  </a>
                ) : s.onClick ? (
                  <button
                    type="button"
                    className={clx(styles.statLink, styles.statButton)}
                    style={s.color ? { color: s.color } : undefined}
                    onClick={s.onClick}
                  >
                    {s.value}
                    <Ico d={ICO_CHEVRIGHT} size={11} strokeWidth={1.8} />
                  </button>
                ) : (
                  <div
                    className={clx(styles.statValue, s.mono && styles.statMono)}
                    style={s.color ? { color: s.color } : undefined}
                  >
                    {s.value}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* body */}
        <div className={styles.body}>
          <div key={frameKey} className={styles.frame}>
            {children}
          </div>
        </div>

        {/* footer */}
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </>
  );
}
